/**
 * A document job as the tray and the AI panel show it (R1 P3′).
 *
 * The backend stores one progress event per fact, after the checkpoint it
 * describes is saved (backend `lib/document-job/progress.ts`), and
 * `/agent/jobs` returns them on each document job as `document`. Everything a
 * person reads about a document job is worded here, from those facts only:
 * "Wrote 3 of 8: Methodology", never a bare "Writing…". No limit or spend
 * figure ever reaches this module.
 *
 * All copy is placeholder (AU English) until Kapil's hand test.
 */

import type { JobPhaseStep } from '@/lib/activity-phases'
import { DOCUMENT_QUESTIONS_ANCHOR } from '@/lib/question-view'
import { UNTITLED_SECTION } from '@/lib/ai-edit-copy'

// ===================
// Types (mirror backend lib/document-job/progress.ts and job-view.ts)
// ===================

export type ProgressSectionResult = 'drafted' | 'saved' | 'suggested' | 'skipped' | 'failed' | 'not_drafted'

export type DocumentJobProgressEvent =
  | { kind: 'researched'; passages: number }
  | { kind: 'planned'; sections: number }
  | {
      kind: 'section'
      index: number
      total: number
      sectionKey: string
      heading: string
      result: ProgressSectionResult
      segmentId?: string
      reason?: string
    }
  | { kind: 'stopped'; reason: 'limit' | 'write_refused'; notDrafted: string[]; message?: string }
  | { kind: 'reviewed'; result: 'checked' | 'skipped' | 'failed'; checked: number; fixes: number }
  | { kind: 'delivered'; saved: number; suggested: number; skipped: number; notDrafted: number; questions: number }

export type DocumentJobCancellation =
  | 'user_cancelled'
  | 'answer_changed'
  | 'bid_deleted'
  | 'artifact_deleted'
  | 'org_deleted'
  | 'other'

export interface DocumentJobView {
  step: 'research' | 'plan' | 'write' | 'review' | 'deliver' | 'handoff' | null
  shape: 'rows' | 'document_row' | null
  events: DocumentJobProgressEvent[]
  queue: { position: number; estimatedStartMinutes: number | null } | null
  outcome: 'full' | 'partial' | null
  notDrafted: string[]
  notChecked: string[]
  cancellationReason: DocumentJobCancellation | null
  /**
   * The recap the job wrote when it ended (Markdown, built by code), or null.
   * Optional: an older server does not send it. Shown on the document
   * overview's card (R1 UX1).
   */
  recap?: string | null
  /**
   * What the review left on each section, once the job has a result: open
   * items with their kind, then writing notes as `could_be_stronger`. Absent
   * when there are none, or from an older server.
   */
  reviewNotes?: DocumentJobReviewNote[]
  /** How many notes the job's caps left out of `reviewNotes` (only the count is stored). */
  reviewNotesMore?: number
}

export type DocumentJobReviewNoteKind = 'missing_fact' | 'unsupported' | 'contradiction' | 'could_be_stronger'

export interface DocumentJobReviewNote {
  /**
   * The same key the job's `section` progress events carry. With `shape:
   * 'rows'` it is the outline's section key; with `'document_row'` (a single
   * document) it is the plan's section id: the note belongs to the one
   * document (`__document__`), in the part named by `heading`.
   */
  sectionKey: string
  heading: string
  kind: DocumentJobReviewNoteKind
  /** As stored: "<heading>: <problem>". */
  text: string
}

/** The fields of a tray job this module reads. */
export interface DocumentJobLike {
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  bidId: string | null
  artifactId: string | null
  errorMessage: string | null
  document?: DocumentJobView
}

/** Distinct states a person can tell apart at a glance. */
export type DocumentJobState = 'waiting' | 'running' | 'saved' | 'partial' | 'failed' | 'cancelled'

// ===================
// Copy helpers
// ===================

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`
}

/** A section's name: its heading, or the editor's fallback ("this section") when the heading is blank. */
function nameOf(heading: string): string {
  return heading.trim() || UNTITLED_SECTION
}

function listOf(items: readonly string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/**
 * R1 J-tune4: an outline section is drafted first and written into the
 * document once its review decides it, so a drafted section says when it will
 * be saved, and has no link until it is (placeholder copy).
 */
export const DRAFTED_AWAITING_SAVE = 'saved once checked'

const SECTION_VERB: Record<ProgressSectionResult, string> = {
  saved: 'Wrote',
  suggested: 'Suggested',
  drafted: 'Drafted',
  skipped: 'Skipped',
  failed: "Couldn't write",
  not_drafted: "Didn't draft",
}

export interface EventLineOptions {
  /** A drafted section not written yet while the job runs: say it is saved once checked. */
  awaitingSave?: boolean
}

/** One saved fact, worded. */
export function eventLine(event: DocumentJobProgressEvent, options: EventLineOptions = {}): string {
  switch (event.kind) {
    case 'researched':
      return event.passages > 0 ? `Found ${plural(event.passages, 'relevant passage', 'relevant passages')}` : 'Finished research'
    case 'planned':
      return `Planned ${plural(event.sections, 'section', 'sections')}`
    case 'section': {
      const line = `${SECTION_VERB[event.result]} ${event.index} of ${event.total}: ${nameOf(event.heading)}`
      return event.result === 'drafted' && options.awaitingSave ? `${line} · ${DRAFTED_AWAITING_SAVE}` : line
    }
    case 'stopped':
      if (event.reason === 'write_refused' && event.message) return event.message
      return event.notDrafted.length > 0 ? `Stopped before ${listOf(event.notDrafted.map(nameOf))}` : 'Stopped before checking the draft'
    case 'reviewed':
      if (event.result === 'failed') return "Couldn't check the draft"
      if (event.result === 'skipped') return "Didn't check the draft"
      return event.fixes > 0
        ? `Checked ${plural(event.checked, 'section', 'sections')} · fixed ${event.fixes}`
        : `Checked ${plural(event.checked, 'section', 'sections')}`
    case 'delivered':
      return deliveredLine(event)
  }
}

function deliveredLine(event: Extract<DocumentJobProgressEvent, { kind: 'delivered' }>): string {
  const parts: string[] = []
  if (event.saved > 0) parts.push(`Wrote ${plural(event.saved, 'section', 'sections')}`)
  if (event.suggested > 0) parts.push(`${plural(event.suggested, 'suggestion', 'suggestions')} to review`)
  if (event.skipped > 0) parts.push(`skipped ${event.skipped}`)
  if (event.questions > 0) parts.push(`${plural(event.questions, 'question', 'questions')} for the team`)
  return parts.length > 0 ? parts.join(' · ') : 'Finished · nothing was written'
}

function ordinalAhead(position: number): string {
  return position <= 1 ? 'next in line' : `${plural(position - 1, 'job', 'jobs')} ahead`
}

const CANCELLED_LINE: Record<DocumentJobCancellation, string> = {
  user_cancelled: 'Cancelled · anything already saved is kept',
  answer_changed: 'Replaced by a newer answer',
  bid_deleted: 'Stopped: the bid was deleted',
  artifact_deleted: 'Stopped: the document was deleted',
  org_deleted: 'Stopped',
  other: 'Cancelled',
}

// ===================
// State and lines
// ===================

export function documentJobState(job: DocumentJobLike): DocumentJobState {
  switch (job.status) {
    case 'pending':
      return 'waiting'
    case 'running':
      return 'running'
    case 'completed':
      return job.document?.outcome === 'partial' ? 'partial' : 'saved'
    case 'failed':
      return 'failed'
    case 'cancelled':
      return 'cancelled'
  }
}

/** The one line the tray shows for a document job. */
export function documentJobLine(job: DocumentJobLike): string {
  const view = job.document
  const latest = view?.events[view.events.length - 1]
  switch (documentJobState(job)) {
    case 'waiting': {
      const queue = view?.queue
      if (!queue) return 'Waiting to start'
      const estimate = queue.estimatedStartMinutes !== null ? ` · starting in about ${queue.estimatedStartMinutes} min` : ''
      return `Waiting · ${ordinalAhead(queue.position)}${estimate}`
    }
    case 'running':
      return latest ? eventLine(latest, { awaitingSave: !hasDelivered(view) }) : 'Started'
    case 'saved': {
      const delivered = view?.events.find((event) => event.kind === 'delivered')
      return delivered ? eventLine(delivered) : 'Finished'
    }
    case 'partial': {
      if (view && view.notDrafted.length > 0) return `Partly done · not drafted: ${listOf(view.notDrafted)}`
      if (view && view.notChecked.length > 0) return `Partly done · not checked: ${listOf(view.notChecked)}`
      return 'Partly done'
    }
    case 'failed':
      return job.errorMessage ?? "Couldn't finish · anything already saved is kept"
    case 'cancelled':
      return CANCELLED_LINE[view?.cancellationReason ?? 'other']
  }
}

type SectionEvent = Extract<DocumentJobProgressEvent, { kind: 'section' }>

function sectionEventsOf(view: DocumentJobView | undefined): SectionEvent[] {
  return (view?.events ?? []).filter((event): event is SectionEvent => event.kind === 'section')
}

function hasDelivered(view: DocumentJobView | undefined): boolean {
  return (view?.events ?? []).some((event) => event.kind === 'delivered')
}

/**
 * Each section once, in the order it first appeared, as its latest fact says
 * (R1 J-tune4: a section drafted, then written when its review decides it).
 */
function latestPerSection(view: DocumentJobView | undefined): SectionEvent[] {
  const latest = new Map<string, SectionEvent>()
  for (const event of sectionEventsOf(view)) latest.set(event.sectionKey, event)
  return [...latest.values()]
}

/** Share of planned sections drafted so far (0 until the first section): the furthest one, so a later write never moves it back. */
export function documentJobProgressPercent(view: DocumentJobView | undefined): number {
  const sections = sectionEventsOf(view)
  const total = sections[0]?.total ?? 0
  if (total === 0) return 0
  const furthest = Math.max(...sections.map((event) => event.index))
  return Math.round((furthest / total) * 100)
}

// ===================
// Phases (the panel's activity rows)
// ===================

/**
 * One phase step per saved fact, worded (activity-phases `hydratePhasesFromJobSteps` aggregates them).
 * A section is one writing step, named by its first fact; a drafted one still waiting to be written
 * says it is saved once checked, unless the job has ended (`ended`).
 */
export function documentJobPhaseSteps(view: DocumentJobView | undefined, options: { ended?: boolean } = {}): JobPhaseStep[] {
  const latest = new Map(latestPerSection(view).map((event) => [event.sectionKey, event]))
  const seen = new Set<string>()
  const delivered = hasDelivered(view)
  return (view?.events ?? []).flatMap((event): JobPhaseStep[] => {
    switch (event.kind) {
      case 'researched':
        return [{ kind: 'reading', status: 'completed', title: eventLine(event) }]
      case 'planned':
        return [{ kind: 'planning', status: 'completed', title: eventLine(event) }]
      case 'section': {
        if (seen.has(event.sectionKey)) return []
        seen.add(event.sectionKey)
        const awaitingSave = !options.ended && !delivered && latest.get(event.sectionKey)?.result === 'drafted'
        return [{ kind: 'writing', status: event.result === 'failed' ? 'failed' : 'completed', title: eventLine(event, { awaitingSave }) }]
      }
      case 'reviewed':
        return [{ kind: 'reviewing', status: event.result === 'failed' ? 'failed' : 'completed', title: eventLine(event) }]
      default:
        // A stop and the delivery are the job's line (documentJobLine), not a phase.
        return []
    }
  })
}

// ===================
// Links
// ===================

export interface SectionLink {
  sectionKey: string
  heading: string
  result: ProgressSectionResult
  reason?: string
  /** Null when nothing was written there (skipped, failed, not drafted). */
  href: string | null
}

export interface DocumentJobLinks {
  sections: SectionLink[]
  /** Suggestions waiting for review — the document's suggestion list. */
  suggestions: { count: number; href: string } | null
  /** Questions the job raised — the document overview's "Questions for this document" (R1 Q2). */
  questions: { count: number; href: string } | null
}

const LINKED_RESULTS: ReadonlySet<ProgressSectionResult> = new Set(['saved', 'suggested'])

function isEnded(status: DocumentJobLike['status']): boolean {
  return status !== 'pending' && status !== 'running'
}


/** Where a section of this job's document opens: its section editor, or a single document's editor. */
function sectionHrefOf(view: DocumentJobView, documentPath: string, sectionKey: string): string {
  return view.shape === 'document_row'
    ? `${documentPath}/document`
    : `${documentPath}/sections/${encodeURIComponent(sectionKey)}`
}

export function documentJobLinks(job: DocumentJobLike): DocumentJobLinks {
  const view = job.document
  if (!view || !job.bidId || !job.artifactId) return { sections: [], suggestions: null, questions: null }
  const documentPath = `/app/bids/${job.bidId}/responses/${job.artifactId}`
  const sectionHref = (sectionKey: string): string => sectionHrefOf(view, documentPath, sectionKey)

  const delivered = hasDelivered(view)
  // A drafted section is in the document only once written (R1 J-tune4); a single document's at its delivery.
  const linked = (event: SectionEvent): boolean =>
    LINKED_RESULTS.has(event.result) || (event.result === 'drafted' && view.shape === 'document_row' && delivered)
  const awaitingSave = (event: SectionEvent): boolean => event.result === 'drafted' && !delivered && !isEnded(job.status)
  const sections: SectionLink[] = latestPerSection(view).map((event) => ({
    sectionKey: event.sectionKey,
    heading: nameOf(event.heading),
    result: event.result,
    ...(event.reason ? { reason: event.reason } : awaitingSave(event) ? { reason: DRAFTED_AWAITING_SAVE } : {}),
    href: linked(event) ? sectionHref(event.sectionKey) : null,
  }))
  const deliveredEvent = view.events.find(
    (event): event is Extract<DocumentJobProgressEvent, { kind: 'delivered' }> => event.kind === 'delivered'
  )
  const suggestionCount = deliveredEvent?.suggested ?? sections.filter((section) => section.result === 'suggested').length
  const questionCount = deliveredEvent?.questions ?? 0
  return {
    sections,
    suggestions: suggestionCount > 0 ? { count: suggestionCount, href: documentPath } : null,
    questions: questionCount > 0
      ? { count: questionCount, href: `${documentPath}#${DOCUMENT_QUESTIONS_ANCHOR}` }
      : null,
  }
}

// ===================
// The overview's one-line recap (draft page redesign)
// ===================

type DeliveredEvent = Extract<DocumentJobProgressEvent, { kind: 'delivered' }>

function deliveredOf(view: DocumentJobView | undefined): DeliveredEvent | undefined {
  return view?.events.find((event): event is DeliveredEvent => event.kind === 'delivered')
}

/** Sections the job wrote or suggested: the delivery's count, else each section once by its latest fact. */
export function draftedSectionCount(job: DocumentJobLike): number {
  const delivered = deliveredOf(job.document)
  if (delivered) return delivered.saved + delivered.suggested
  return latestPerSection(job.document).filter((event) => LINKED_RESULTS.has(event.result)).length
}

/**
 * A finished job that drafted something: the overview shows its one-line
 * recap. Every other state (waiting, running, failed, cancelled, nothing
 * drafted) keeps the job's own card.
 */
export function hasDraftedRecap(job: DocumentJobLike): boolean {
  return job.status === 'completed' && draftedSectionCount(job) > 0
}

export interface RecapCounts {
  drafted: number
  suggestions: number
  questions: number
}

/** "Drafted 3 sections · 3 suggestions to review · 8 questions for your team", each part left out at zero. */
export function recapLineParts(counts: RecapCounts): string[] {
  const parts: string[] = []
  if (counts.drafted > 0) parts.push(`Drafted ${plural(counts.drafted, 'section', 'sections')}`)
  if (counts.suggestions > 0) parts.push(`${plural(counts.suggestions, 'suggestion', 'suggestions')} to review`)
  if (counts.questions > 0) parts.push(`${plural(counts.questions, 'question', 'questions')} for your team`)
  return parts
}

/**
 * Where "Review suggestions" goes: the first pending suggestion the page knows
 * of (`pendingSectionKeys`, in the page's order), or, before the page has read
 * them (null), the first section the job suggested. Null when nothing waits.
 */
export function firstSuggestionHref(job: DocumentJobLike, pendingSectionKeys: ReadonlyArray<string> | null): string | null {
  const view = job.document
  if (!view || !job.bidId || !job.artifactId) return null
  const documentPath = `/app/bids/${job.bidId}/responses/${job.artifactId}`
  const sectionKey = pendingSectionKeys
    ? pendingSectionKeys[0]
    : latestPerSection(view).find((event) => event.result === 'suggested')?.sectionKey
  return sectionKey === undefined ? null : sectionHrefOf(view, documentPath, sectionKey)
}

/**
 * The opening paragraph of a job's recap (everything before its first blank
 * line). The AI panel shows only this: the recap's lists — what the review
 * left open, the questions — live on the document overview.
 */
export function recapOpeningParagraph(recap: string): string {
  return recap.trim().split(/\n[ \t]*\n/)[0]?.trim() ?? ''
}

// ===================
// Reviewer notes on a suggestion (draft page redesign)
// ===================

/** The single document's one suggestion key (`types/scope` DOCUMENT_SECTION_KEY). */
const SINGLE_DOCUMENT_KEY = '__document__'

/**
 * A note's problem, without the "<heading>: " it is stored with. Only that
 * exact prefix is removed (a blank heading is stored as ": problem"); the
 * text is never split on its first colon, which may be part of the problem.
 */
export function reviewNoteText(note: DocumentJobReviewNote): string {
  const trimmed = note.text.trim()
  const prefix = `${note.heading.trim()}: `
  return trimmed.startsWith(prefix) ? trimmed.slice(prefix.length).trim() : trimmed
}

export interface SuggestionNoteLine {
  /** The part of a single document the note is about; null for an outline section, whose strip names it already. */
  label: string | null
  text: string
}

export interface SuggestionNotes {
  notes: SuggestionNoteLine[]
  /** Notes the job's caps left out (a single document only: the count is job-wide). */
  more: number
}

/** The review strip's suggestion, as far as the notes need it. */
export interface SuggestionForNotes {
  sectionId: string
  mode?: 'staged' | 'applied' | undefined
  /** The turn that made it: null for a document job's suggestion; absent when unknown. */
  turnId?: string | null | undefined
}

const NO_NOTES: SuggestionNotes = { notes: [], more: 0 }

/**
 * The reviewer's notes for the suggestion on screen, from the document's
 * latest job (a snapshot taken when it ended). Only a job's own staged
 * suggestion gets them (no turn made it), and only one that job left:
 * - an outline document: the notes on that section, when the job's last word
 *   on it was "suggested";
 * - a single document: the one `__document__` suggestion holds every part the
 *   job wrote, so it gets all the job's notes, each labelled with its part.
 * Gaps come first, as the server sends them.
 */
export function reviewNotesForSuggestion(job: DocumentJobLike | null | undefined, suggestion: SuggestionForNotes): SuggestionNotes {
  const view = job?.document
  if (!job || !view || !isEnded(job.status)) return NO_NOTES
  if (suggestion.mode !== 'staged' || suggestion.turnId !== null) return NO_NOTES
  const notes = view.reviewNotes ?? []
  if (notes.length === 0) return NO_NOTES

  if (view.shape === 'document_row') {
    if (suggestion.sectionId !== SINGLE_DOCUMENT_KEY || draftedSectionCount(job) === 0) return NO_NOTES
    return {
      notes: notes.map((note) => ({ label: note.heading.trim() || null, text: reviewNoteText(note) })),
      more: view.reviewNotesMore ?? 0,
    }
  }

  const latest = latestPerSection(view).find((event) => event.sectionKey === suggestion.sectionId)
  if (latest?.result !== 'suggested') return NO_NOTES
  return {
    notes: notes
      .filter((note) => note.sectionKey === suggestion.sectionId)
      .map((note) => ({ label: null, text: reviewNoteText(note) })),
    more: 0,
  }
}
